import { Plus, Trash2, GripVertical } from "lucide-react";
import { ImageSlot } from "./ImageSlot";
import { DragHandleList } from "./DragHandleList";

const In = ({ label, ...p }) => (
  <div>
    {label && <label className="mb-1 block text-[11px] text-amber-100/50">{label}</label>}
    <input
      {...p}
      className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50"
    />
  </div>
);
const Area = ({ label, ...p }) => (
  <div>
    {label && <label className="mb-1 block text-[11px] text-amber-100/50">{label}</label>}
    <textarea
      {...p}
      className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50"
    />
  </div>
);
const Grip = (props) => (
  <button {...props} className="cursor-grab rounded p-1 text-amber-100/30 hover:text-amber-200 active:cursor-grabbing">
    <GripVertical className="h-4 w-4" />
  </button>
);

// Settings body for a selected journey module (rendered inside the editor's
// right drawer). Content lists (videos, gallery, cantos, steps) are drag
// reorderable via the DragHandleList.
export const ModuleSettings = ({ module: m, patch, campaignId }) => {
  const header = (
    <>
      <In label="Section title" value={m.title || ""} onChange={(e) => patch({ title: e.target.value })} />
      <In label="Section subtitle" value={m.subtitle || ""} onChange={(e) => patch({ subtitle: e.target.value })} />
    </>
  );

  if (m.type === "conversion") {
    return (
      <div className="grid gap-3">
        <In label="Headline" value={m.headline || ""} onChange={(e) => patch({ headline: e.target.value })} />
        <Area label="Body" rows={2} value={m.body || ""} onChange={(e) => patch({ body: e.target.value })} />
        <In label="WhatsApp button label" value={m.wa_label || ""} onChange={(e) => patch({ wa_label: e.target.value })} />
        <Area
          label="Pre-filled WhatsApp message (use {name} for the visitor's name)"
          rows={3}
          value={m.wa_message || ""}
          onChange={(e) => patch({ wa_message: e.target.value })}
        />
        <p className="text-[11px] text-amber-100/40">
          An <span className="text-amber-200/70">Interested - Path · slug</span> tag is added
          automatically so GiriChat can segment leads.
        </p>
        <label className="flex items-center gap-2 text-sm text-amber-100/70">
          <input
            type="checkbox"
            checked={m.book_enabled !== false}
            onChange={(e) => patch({ book_enabled: e.target.checked })}
            className="h-4 w-4 accent-amber-400"
          />
          Show &quot;Book Your Copy&quot; link
        </label>
        <In value={m.book_label || ""} onChange={(e) => patch({ book_label: e.target.value })} placeholder="Book Your Copy Directly" />
      </div>
    );
  }

  if (m.type === "video_carousel") {
    const videos = m.videos || [];
    return (
      <div className="grid gap-3">
        {header}
        <label className="text-[11px] text-amber-100/50">Video links (drag to reorder)</label>
        <DragHandleList
          className="space-y-2"
          items={videos}
          getKey={(_, i) => i}
          onReorder={(next) => patch({ videos: next })}
          renderItem={(v, i, dh) => (
            <div className="flex items-center gap-2">
              <Grip {...dh} />
              <input
                value={v}
                onChange={(e) => patch({ videos: videos.map((x, xi) => (xi === i ? e.target.value : x)) })}
                placeholder="https://youtube.com/shorts/… or facebook/instagram reel"
                className="flex-1 rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50"
              />
              <button onClick={() => patch({ videos: videos.filter((_, xi) => xi !== i) })} className="rounded-full border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        />
        <button onClick={() => patch({ videos: [...videos, ""] })} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10">
          <Plus className="h-3.5 w-3.5" /> Add video
        </button>
      </div>
    );
  }

  if (m.type === "audio_sample") {
    return (
      <div className="grid gap-3">
        {header}
        <In label="Audio URL" value={m.src || ""} onChange={(e) => patch({ src: e.target.value })} placeholder="https://…/mantra.mp3" />
      </div>
    );
  }

  if (m.type === "mala_counter") return <div className="grid gap-3">{header}</div>;

  if (m.type === "gallery") {
    const images = m.images || [];
    return (
      <div className="grid gap-3">
        {header}
        <label className="text-[11px] text-amber-100/50">Images (drag to reorder)</label>
        <DragHandleList
          className="space-y-2"
          items={images}
          getKey={(_, i) => i}
          onReorder={(next) => patch({ images: next })}
          renderItem={(img, i, dh) => (
            <div className="flex items-center gap-2">
              <Grip {...dh} />
              <div className="w-16 shrink-0">
                <ImageSlot campaignId={campaignId} aspect="aspect-[4/5]" value={img} onChange={(v) => patch({ images: images.map((x, xi) => (xi === i ? v : x)) })} testId={`gallery-slot-${m.id}-${i}`} />
              </div>
              <button onClick={() => patch({ images: images.filter((_, xi) => xi !== i) })} className="rounded-full border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        />
        <button onClick={() => patch({ images: [...images, ""] })} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10">
          <Plus className="h-3.5 w-3.5" /> Add image
        </button>
      </div>
    );
  }

  if (m.type === "photo_story") {
    const steps = m.steps || [];
    const setStep = (i, ps) => patch({ steps: steps.map((s, si) => (si === i ? { ...s, ...ps } : s)) });
    return (
      <div className="grid gap-3">
        {header}
        <label className="text-[11px] text-amber-100/50">Steps (drag to reorder)</label>
        <DragHandleList
          className="space-y-2"
          items={steps}
          getKey={(_, i) => i}
          onReorder={(next) => patch({ steps: next })}
          renderItem={(s, i, dh) => (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300/10 p-2">
              <div className="pt-2"><Grip {...dh} /></div>
              <div className="w-20 shrink-0">
                <ImageSlot campaignId={campaignId} aspect="aspect-video" value={s.image} onChange={(v) => setStep(i, { image: v })} testId={`story-slot-${m.id}-${i}`} />
              </div>
              <input value={s.caption || ""} onChange={(e) => setStep(i, { caption: e.target.value })} placeholder={`Step ${i + 1} caption`} className="mt-1 flex-1 rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50" />
              <button onClick={() => patch({ steps: steps.filter((_, si) => si !== i) })} className="mt-1 rounded-full border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        />
        <button onClick={() => patch({ steps: [...steps, { image: "", caption: "" }] })} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10">
          <Plus className="h-3.5 w-3.5" /> Add step
        </button>
      </div>
    );
  }

  if (m.type === "canto_carousel") {
    const cantos = m.cantos || [];
    const setC = (i, pc) => patch({ cantos: cantos.map((c, ci) => (ci === i ? { ...c, ...pc } : c)) });
    return (
      <div className="grid gap-3">
        {header}
        <label className="text-[11px] text-amber-100/50">Cantos (drag to reorder)</label>
        <DragHandleList
          className="space-y-2"
          items={cantos}
          getKey={(c, i) => c.id || i}
          onReorder={(next) => patch({ cantos: next })}
          renderItem={(c, i, dh) => (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300/10 p-2">
              <div className="pt-2"><Grip {...dh} /></div>
              <div className="w-16 shrink-0">
                <ImageSlot campaignId={campaignId} aspect="aspect-[3/4]" value={c.cover} onChange={(v) => setC(i, { cover: v })} testId={`canto-slot-${m.id}-${i}`} />
              </div>
              <div className="flex-1 space-y-2">
                <input value={c.title || ""} onChange={(e) => setC(i, { title: e.target.value })} placeholder="Canto title" className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50" />
                <textarea rows={2} value={(c.lines || []).join("\n")} onChange={(e) => setC(i, { lines: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} placeholder="Story lines (one per line)" className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50" />
              </div>
              <button onClick={() => patch({ cantos: cantos.filter((_, ci) => ci !== i) })} className="mt-1 rounded-full border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        />
        <button onClick={() => patch({ cantos: [...cantos, { id: `canto-${Date.now()}`, cover: "", title: "", lines: [] }] })} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10">
          <Plus className="h-3.5 w-3.5" /> Add canto
        </button>
      </div>
    );
  }

  return <div className="grid gap-3">{header}</div>;
};
